import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import {
  CircleNotch, ArrowLeft, ArrowRight, FloppyDisk, RocketLaunch,
  Plus, Trash, Check, X, Warning, Lightbulb,
} from "@phosphor-icons/react";
import CoPilotPanel, { CoPilotToggle } from "@/components/CoPilotPanel";
import PhaseStepper from "@/components/PhaseStepper";

const STEPS = [
  { n: 1, code: "F", label: "Fact-Find & Context", phase: "FOCUS" },
  { n: 2, code: "O", label: "Objectives & Intent", phase: "FOCUS" },
  { n: 3, code: "C", label: "Course of Action", phase: "FOCUS" },
  { n: 4, code: "U", label: "Understand Risks", phase: "FOCUS" },
  { n: 5, code: "S", label: "Select & Set Method", phase: "FOCUS" },
  { n: 6, code: "A", label: "Alignment & Objective", phase: "ARMOR" },
  { n: 7, code: "R", label: "Responsibilities & Method", phase: "ARMOR" },
  { n: 8, code: "M", label: "Marches & Milestones", phase: "ARMOR" },
  { n: 9, code: "O", label: "Operational Boundaries", phase: "ARMOR" },
  { n: 10, code: "R", label: "Resources & Enablers", phase: "ARMOR" },
];

const LAST_STEP = STEPS.length;

const uid = (p) => p + "_" + Math.random().toString(36).slice(2, 10);

const emptyFocus = () => ({
  factFind: "",
  strategicIntent: "",
  operationalObjectives: "",
  coas: ["", "", ""],
  risks: [{ opportunity: "", risk: "" }, { opportunity: "", risk: "" }, { opportunity: "", risk: "" }],
  selectedOption: "",
  gatekeepers: { valueAlignment: false, objectiveCertainty: false, authorityBaseline: "Local Autonomy" },
});

const emptyArmor = () => ({
  alignment: "",
  phases: "",
  workingGroups: [],
  milestones: { deadlines: "", milestones: "", reviewCadence: "" },
  boundaries: [],
  resources: { internal: "", externalSMEs: "", kpis: "" },
});

const labelCls = "block text-xs font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1.5";
const inputCls = "flex h-10 w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";
const taCls = "w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export default function Wizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [focus, setFocus] = useState(emptyFocus());
  const [armor, setArmor] = useState(emptyArmor());
  const [coPilotOpen, setCoPilotOpen] = useState(false);

  // Append a Co-Pilot suggestion to the primary field of the active step (never overwrites other inputs)
  const appendText = (existing, addition) =>
    !existing || !existing.trim() ? addition : `${existing.trimEnd()}\n- ${addition}`;

  const applySuggestion = (text) => {
    setFocus((fPrev) => {
      const f = { ...fPrev };
      if (step === 1) f.factFind = appendText(f.factFind, text);
      else if (step === 2) f.strategicIntent = appendText(f.strategicIntent, text);
      else if (step === 3) {
        const coas = [...f.coas];
        const idx = coas.findIndex((c) => !c || !c.trim());
        coas[idx === -1 ? 0 : idx] = idx === -1 ? appendText(coas[0], text) : text;
        f.coas = coas;
      } else if (step === 4) {
        const risks = [...f.risks];
        risks[0] = { ...(risks[0] || {}), risk: appendText(risks[0]?.risk, text) };
        f.risks = risks;
      } else if (step === 5) f.selectedOption = appendText(f.selectedOption, text);
      return f;
    });
    setArmor((aPrev) => {
      const a = { ...aPrev };
      if (step === 6) a.alignment = appendText(a.alignment, text);
      else if (step === 7) a.phases = appendText(a.phases, text);
      else if (step === 8) a.milestones = { ...a.milestones, milestones: appendText(a.milestones.milestones, text) };
      else if (step === 9 && a.boundaries.length) {
        const boundaries = [...a.boundaries];
        boundaries[0] = { ...(boundaries[0] || {}), freedoms: appendText(boundaries[0]?.freedoms, text) };
        a.boundaries = boundaries;
      } else if (step === 10) a.resources = { ...a.resources, kpis: appendText(a.resources.kpis, text) };
      return a;
    });
    if (step === 9 && armor.boundaries.length === 0) {
      navigator.clipboard?.writeText(text);
      toast.info("Add a Working Group first — suggestion copied to clipboard");
    } else {
      toast.success("Suggestion added to this step");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/documents/${id}`);
        setTitle(res.data.title || "");
        const p = res.data.payload || {};
        setFocus({ ...emptyFocus(), ...(p.focus || {}) });
        setArmor({ ...emptyArmor(), ...(p.armor || {}) });
      } catch {
        toast.error("Assessment not found");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const persist = useCallback(async (extra = {}) => {
    setSaving(true);
    try {
      await api.put(`/documents/${id}`, { title: title || "Untitled Assessment", payload: { focus, armor }, ...extra });
      return true;
    } catch {
      toast.error("Save failed");
      return false;
    } finally {
      setSaving(false);
    }
  }, [id, title, focus, armor]);

  const saveDraft = async () => {
    const ok = await persist();
    if (ok) toast.success("Draft saved");
  };

  const next = async () => {
    await persist();
    setStep((s) => Math.min(LAST_STEP, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finalise = async () => {
    const ok = await persist({ status: "Active Execution" });
    if (ok) {
      toast.success("Plan launched into Active Execution");
      navigate("/dashboard");
    }
  };

  // ---- working group helpers ----
  const addGroup = () =>
    setArmor((a) => ({
      ...a,
      workingGroups: [...a.workingGroups, { id: uid("wg"), name: "", tasks: [] }],
      boundaries: [...a.boundaries, { freedoms: "", constraints: "" }],
    }));
  const removeGroup = (idx) =>
    setArmor((a) => ({
      ...a,
      workingGroups: a.workingGroups.filter((_, i) => i !== idx),
      boundaries: a.boundaries.filter((_, i) => i !== idx),
    }));
  const setGroupName = (idx, name) =>
    setArmor((a) => ({ ...a, workingGroups: a.workingGroups.map((g, i) => (i === idx ? { ...g, name } : g)) }));
  const addTask = (idx) =>
    setArmor((a) => ({
      ...a,
      workingGroups: a.workingGroups.map((g, i) =>
        i === idx ? { ...g, tasks: [...g.tasks, { id: uid("tsk"), name: "", status: false }] } : g
      ),
    }));
  const setTaskName = (gi, ti, name) =>
    setArmor((a) => ({
      ...a,
      workingGroups: a.workingGroups.map((g, i) =>
        i === gi ? { ...g, tasks: g.tasks.map((t, j) => (j === ti ? { ...t, name } : t)) } : g
      ),
    }));
  const removeTask = (gi, ti) =>
    setArmor((a) => ({
      ...a,
      workingGroups: a.workingGroups.map((g, i) =>
        i === gi ? { ...g, tasks: g.tasks.filter((_, j) => j !== ti) } : g
      ),
    }));

  if (loading)
    return (
      <Layout>
        <div className="flex items-center justify-center py-40 text-slate-400">
          <CircleNotch size={28} className="animate-spin" />
        </div>
      </Layout>
    );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto w-full py-8 px-6 lg:px-10">
        {/* Title */}
        <input
          data-testid="assessment-title-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Assessment title…"
          className="w-full bg-transparent text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 placeholder:text-slate-300 focus:outline-none border-b border-transparent focus:border-slate-300 pb-1"
        />

        {/* Stepper — two stacked phase bars (active phase expanded) */}
        <PhaseStepper steps={STEPS} currentStep={step} onSelect={setStep} />

        {/* Step content */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-6 animate-[fadeIn_0.2s_ease-in]">
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">F — Fact-Find &amp; Context</h2>
              <p className="text-sm text-slate-500">Describe the internal and external environment, current situation, and any background constraints relevant to the problem.</p>
              <Field label="Situation & Context">
                <textarea data-testid="field-factFind" rows={10} className={taCls} value={focus.factFind}
                  onChange={(e) => setFocus({ ...focus, factFind: e.target.value })}
                  placeholder="What is the current situation? Background, constraints, resources, stakeholders, external pressures…" />
              </Field>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">O — Objectives &amp; Intent</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <Field label="Strategic Intent — Why">
                  <textarea data-testid="field-strategicIntent" rows={8} className={taCls} value={focus.strategicIntent}
                    onChange={(e) => setFocus({ ...focus, strategicIntent: e.target.value })}
                    placeholder="The overarching purpose and desired end-state…" />
                </Field>
                <Field label="Operational Objectives — What / When / Who">
                  <textarea data-testid="field-operationalObjectives" rows={8} className={taCls} value={focus.operationalObjectives}
                    onChange={(e) => setFocus({ ...focus, operationalObjectives: e.target.value })}
                    placeholder="Concrete, measurable objectives with owners and deadlines…" />
                </Field>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">C — Course of Action Development</h2>
              <p className="text-sm text-slate-500">Map out up to three distinct options.</p>
              {focus.coas.map((c, i) => (
                <Field key={i} label={`Course of Action ${i + 1}`}>
                  <textarea data-testid={`field-coa-${i}`} rows={3} className={taCls} value={c}
                    onChange={(e) => {
                      const coas = [...focus.coas];
                      coas[i] = e.target.value;
                      setFocus({ ...focus, coas });
                    }}
                    placeholder={`Describe option ${i + 1}…`} />
                </Field>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">U — Understand Risks &amp; Opportunities</h2>
              <p className="text-sm text-slate-500">Evaluate each defined Course of Action.</p>
              <div className="space-y-4">
                {focus.coas.map((c, i) => (
                  <div key={i} className="border border-slate-200 rounded-sm overflow-hidden">
                    <div className="bg-slate-50 px-4 py-2 border-b border-slate-200">
                      <span className="font-mono text-xs font-semibold text-slate-600">COA {i + 1}</span>
                      <span className="ml-2 text-sm text-slate-500">{c || "— not defined —"}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-1.5 text-emerald-700"><Lightbulb size={16} weight="duotone" /><span className="text-xs font-semibold uppercase tracking-wide">Opportunities</span></div>
                        <textarea data-testid={`field-opportunity-${i}`} rows={3} className={taCls} value={focus.risks[i]?.opportunity || ""}
                          onChange={(e) => {
                            const risks = [...focus.risks];
                            risks[i] = { ...(risks[i] || {}), opportunity: e.target.value };
                            setFocus({ ...focus, risks });
                          }} />
                      </div>
                      <div className="p-4">
                        <div className="flex items-center gap-2 mb-1.5 text-red-700"><Warning size={16} weight="duotone" /><span className="text-xs font-semibold uppercase tracking-wide">Operational Risks</span></div>
                        <textarea data-testid={`field-risk-${i}`} rows={3} className={taCls} value={focus.risks[i]?.risk || ""}
                          onChange={(e) => {
                            const risks = [...focus.risks];
                            risks[i] = { ...(risks[i] || {}), risk: e.target.value };
                            setFocus({ ...focus, risks });
                          }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">S — Select &amp; Set Method</h2>
              <Field label="Preferred Option Summary">
                <textarea data-testid="field-selectedOption" rows={5} className={taCls} value={focus.selectedOption}
                  onChange={(e) => setFocus({ ...focus, selectedOption: e.target.value })}
                  placeholder="Which option is chosen and why…" />
              </Field>
              <div className="space-y-3">
                <p className={labelCls}>Gatekeeper Checks</p>
                <GateToggle testid="gate-valueAlignment" label="Value Alignment" desc="Does this align with organisational values?"
                  value={focus.gatekeepers.valueAlignment}
                  onChange={(v) => setFocus({ ...focus, gatekeepers: { ...focus.gatekeepers, valueAlignment: v } })} />
                <GateToggle testid="gate-objectiveCertainty" label="Objective Certainty" desc="Are the objectives clearly achievable?"
                  value={focus.gatekeepers.objectiveCertainty}
                  onChange={(v) => setFocus({ ...focus, gatekeepers: { ...focus.gatekeepers, objectiveCertainty: v } })} />
                <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Authority Baseline</p>
                    <p className="text-xs text-slate-500">Decision authority level</p>
                  </div>
                  <div className="inline-flex rounded-sm bg-slate-100 p-1">
                    {["Local Autonomy", "Requires Escalation"].map((opt) => (
                      <button key={opt} data-testid={`authority-${opt === "Local Autonomy" ? "local" : "escalation"}`}
                        onClick={() => setFocus({ ...focus, gatekeepers: { ...focus.gatekeepers, authorityBaseline: opt } })}
                        className={`px-3 py-1.5 rounded-sm text-sm font-medium transition-all ${focus.gatekeepers.authorityBaseline === opt ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">A — Alignment &amp; Objective</h2>
              <p className="text-sm text-slate-500">Restate the chosen intent so everyone executing is aligned to the same objective.</p>
              <Field label="Alignment & Objective">
                <textarea data-testid="field-alignment" rows={6} className={taCls} value={armor.alignment}
                  onChange={(e) => setArmor({ ...armor, alignment: e.target.value })}
                  placeholder="Echo the chosen intent and objective from the FOCUS phase…" />
                {focus.strategicIntent && !armor.alignment && (
                  <button data-testid="copy-intent" onClick={() => setArmor({ ...armor, alignment: focus.strategicIntent })}
                    className="mt-2 text-xs text-blue-700 hover:underline">Copy from Strategic Intent</button>
                )}
              </Field>
            </div>
          )}

          {step === 7 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">R — Responsibilities &amp; Method</h2>
              <p className="text-sm text-slate-500">Set out the high-level phases, then create Working Groups and assign their tasks.</p>
              <Field label="High-Level Phases">
                <textarea data-testid="field-phases" rows={3} className={taCls} value={armor.phases}
                  onChange={(e) => setArmor({ ...armor, phases: e.target.value })}
                  placeholder="Outline the major phases of execution…" />
              </Field>

              <div className="flex items-center justify-between pt-2">
                <p className={labelCls}>Working Groups &amp; Tasks</p>
                <button data-testid="add-group-button" onClick={addGroup}
                  className="inline-flex items-center gap-1.5 rounded-sm border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
                  <Plus size={16} /> Add Group
                </button>
              </div>

              {armor.workingGroups.length === 0 && (
                <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">No working groups yet. Add one to assign tasks.</p>
              )}

              {armor.workingGroups.map((g, gi) => (
                <div key={g.id} className="border border-slate-200 rounded-sm">
                  <div className="flex items-center gap-2 p-3 bg-slate-50 border-b border-slate-200">
                    <input data-testid={`group-name-${gi}`} value={g.name} onChange={(e) => setGroupName(gi, e.target.value)}
                      placeholder={`Working Group ${gi + 1} name…`} className={inputCls + " flex-1 bg-white"} />
                    <button data-testid={`remove-group-${gi}`} onClick={() => removeGroup(gi)}
                      className="p-2 text-slate-400 hover:text-red-600"><Trash size={16} /></button>
                  </div>
                  <div className="p-3 space-y-2">
                    {g.tasks.map((t, ti) => (
                      <div key={t.id} className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-400 w-5">{ti + 1}.</span>
                        <input data-testid={`task-name-${gi}-${ti}`} value={t.name} onChange={(e) => setTaskName(gi, ti, e.target.value)}
                          placeholder="Task description…" className={inputCls + " flex-1"} />
                        <button data-testid={`remove-task-${gi}-${ti}`} onClick={() => removeTask(gi, ti)}
                          className="p-2 text-slate-400 hover:text-red-600"><X size={16} /></button>
                      </div>
                    ))}
                    <button data-testid={`add-task-${gi}`} onClick={() => addTask(gi)}
                      className="inline-flex items-center gap-1.5 text-sm text-blue-700 hover:underline mt-1"><Plus size={14} /> Add task</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 8 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">M — Marches &amp; Milestones</h2>
              <p className="text-sm text-slate-500">Define the timeline: hard deadlines, key milestones, and the review cadence.</p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Field label="Hard Deadlines">
                  <textarea data-testid="field-deadlines" rows={4} className={taCls} value={armor.milestones.deadlines}
                    onChange={(e) => setArmor({ ...armor, milestones: { ...armor.milestones, deadlines: e.target.value } })} />
                </Field>
                <Field label="Key Milestones">
                  <textarea data-testid="field-milestones" rows={4} className={taCls} value={armor.milestones.milestones}
                    onChange={(e) => setArmor({ ...armor, milestones: { ...armor.milestones, milestones: e.target.value } })} />
                </Field>
                <Field label="Review Cadence">
                  <textarea data-testid="field-reviewCadence" rows={4} className={taCls} value={armor.milestones.reviewCadence}
                    onChange={(e) => setArmor({ ...armor, milestones: { ...armor.milestones, reviewCadence: e.target.value } })} />
                </Field>
              </div>
            </div>
          )}

          {step === 9 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">O — Operational Boundaries</h2>
              <p className="text-sm text-slate-500">For each Working Group, define their Freedoms (what they can do) and Constraints (what they cannot do).</p>
              {armor.workingGroups.length === 0 ? (
                <p className="text-sm text-slate-400 border border-dashed border-slate-300 rounded-sm p-4 text-center">Define working groups in step 7 (R — Responsibilities) first.</p>
              ) : (
                <div className="space-y-3">
                  {armor.workingGroups.map((g, gi) => (
                    <div key={g.id} className="border border-slate-200 rounded-sm">
                      <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 font-mono text-xs font-semibold text-slate-600">
                        {g.name || `Working Group ${gi + 1}`}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200">
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1.5 text-emerald-700"><Check size={16} weight="bold" /><span className="text-xs font-semibold uppercase tracking-wide">Freedoms</span></div>
                          <textarea data-testid={`field-freedoms-${gi}`} rows={2} className={taCls} value={armor.boundaries[gi]?.freedoms || ""}
                            onChange={(e) => {
                              const boundaries = [...armor.boundaries];
                              boundaries[gi] = { ...(boundaries[gi] || {}), freedoms: e.target.value };
                              setArmor({ ...armor, boundaries });
                            }} placeholder="What they can do…" />
                        </div>
                        <div className="p-4">
                          <div className="flex items-center gap-2 mb-1.5 text-red-700"><X size={16} weight="bold" /><span className="text-xs font-semibold uppercase tracking-wide">Constraints</span></div>
                          <textarea data-testid={`field-constraints-${gi}`} rows={2} className={taCls} value={armor.boundaries[gi]?.constraints || ""}
                            onChange={(e) => {
                              const boundaries = [...armor.boundaries];
                              boundaries[gi] = { ...(boundaries[gi] || {}), constraints: e.target.value };
                              setArmor({ ...armor, boundaries });
                            }} placeholder="What they cannot do…" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 10 && (
            <div className="space-y-5">
              <h2 className="text-xl font-semibold text-slate-900">R — Resources &amp; Enablers</h2>
              <p className="text-sm text-slate-500">Identify the resources, expertise, and measures that will enable and track success.</p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Field label="Internal Resources">
                  <textarea data-testid="field-internal" rows={4} className={taCls} value={armor.resources.internal}
                    onChange={(e) => setArmor({ ...armor, resources: { ...armor.resources, internal: e.target.value } })} />
                </Field>
                <Field label="External Subject-Matter Experts">
                  <textarea data-testid="field-externalSMEs" rows={4} className={taCls} value={armor.resources.externalSMEs}
                    onChange={(e) => setArmor({ ...armor, resources: { ...armor.resources, externalSMEs: e.target.value } })} />
                </Field>
                <Field label="Tracked KPIs">
                  <textarea data-testid="field-kpis" rows={4} className={taCls} value={armor.resources.kpis}
                    onChange={(e) => setArmor({ ...armor, resources: { ...armor.resources, kpis: e.target.value } })} />
                </Field>
              </div>
            </div>
          )}
        </div>

        {/* Nav controls */}
        <div className="mt-6 flex items-center justify-between gap-3 sticky bottom-0 bg-slate-50 py-4">
          <button data-testid="wizard-back" onClick={back} disabled={step === 1}
            className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-3">
            <button data-testid="wizard-save-draft" onClick={saveDraft} disabled={saving}
              className="inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <FloppyDisk size={16} />} Save Draft
            </button>
            {step < LAST_STEP ? (
              <button data-testid="wizard-next" onClick={next}
                className="inline-flex items-center gap-2 rounded-sm bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800">
                Next Step <ArrowRight size={16} />
              </button>
            ) : (
              <button data-testid="wizard-finalise" onClick={finalise} disabled={saving}
                className="inline-flex items-center gap-2 rounded-sm bg-emerald-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-700">
                <RocketLaunch size={18} weight="fill" /> Finalise &amp; Launch Plan
              </button>
            )}
          </div>
        </div>
      </div>

      <CoPilotToggle open={coPilotOpen} onClick={() => setCoPilotOpen(true)} phase={STEPS[step - 1].phase} />
      <CoPilotPanel
        open={coPilotOpen}
        onClose={() => setCoPilotOpen(false)}
        docId={id}
        toolType="focus-armor"
        step={STEPS[step - 1]}
        phase={STEPS[step - 1].phase}
        onApplySuggestion={applySuggestion}
      />
    </Layout>
  );
}

const GateToggle = ({ testid, label, desc, value, onChange }) => (
  <div className="flex items-center justify-between border border-slate-200 rounded-sm p-4">
    <div>
      <p className="text-sm font-medium text-slate-900">{label}</p>
      <p className="text-xs text-slate-500">{desc}</p>
    </div>
    <div className="inline-flex rounded-sm bg-slate-100 p-1">
      <button data-testid={`${testid}-yes`} onClick={() => onChange(true)}
        className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-all ${value ? "bg-emerald-600 text-white" : "text-slate-500"}`}>Yes</button>
      <button data-testid={`${testid}-no`} onClick={() => onChange(false)}
        className={`px-4 py-1.5 rounded-sm text-sm font-medium transition-all ${!value ? "bg-slate-900 text-white" : "text-slate-500"}`}>No</button>
    </div>
  </div>
);
